import { GripVertical, Settings, X } from 'lucide-react'
import { useRef, useState } from 'react'

import { getAllIndicators, getIndicator } from '../../indicators/registry'
import { useCustomIndicatorStore } from '../../stores/customIndicatorStore'
import { usePineStore } from '../../stores/pineStore'
import type { ActiveIndicator, AttachedCustomIndicator } from '../../types/chart'
import type { IndicatorCategory } from '../../types/indicator'
import { cn } from '../../utils/cn'

// ── Types ─────────────────────────────────────────────────────────────────────

interface IndicatorUpdate {
  params?: Record<string, number | string | boolean>
  colors?: Record<string, string>
}

interface Props {
  panelId: string
  builtinIndicators: ActiveIndicator[]
  customInstances: AttachedCustomIndicator[]
  onAddBuiltin: (ind: Omit<ActiveIndicator, 'instanceId'>) => void
  onRemoveBuiltin: (instanceId: string) => void
  onUpdateBuiltin: (instanceId: string, updates: IndicatorUpdate) => void
  onToggleBuiltin: (instanceId: string) => void
  onReorderBuiltin: (fromIndex: number, toIndex: number) => void
  onAttachCustom: (definitionId: string) => void
  onDetachCustom: (instanceId: string) => void
  onToggleCustom: (instanceId: string) => void
  onReorderCustom: (fromIndex: number, toIndex: number) => void
  onClose: () => void
}

const CATEGORY_LABELS: Record<IndicatorCategory, string> = {
  trend: 'Trend',
  momentum: 'Momentum',
  volatility: 'Volatility',
  volume: 'Volume',
  professional: 'Professional',
}

const CATEGORY_ORDER: IndicatorCategory[] = [
  'trend',
  'momentum',
  'volatility',
  'volume',
  'professional',
]

function defaultParams(
  config: NonNullable<ReturnType<typeof getIndicator>>['config']
): Record<string, number | string | boolean> {
  return Object.fromEntries(config.params.map((p) => [p.key, p.default]))
}

// ── Built-in indicator settings panel ─────────────────────────────────────────

function BuiltinSettings({
  ind,
  onUpdate,
}: {
  ind: ActiveIndicator
  onUpdate: (u: IndicatorUpdate) => void
}) {
  const entry = getIndicator(ind.id)
  if (!entry) return null
  const { config } = entry
  return (
    <div className="space-y-1.5 rounded border border-tv-border bg-tv-bg-primary p-3">
      {config.params.map((p) => {
        const val = ind.params[p.key] ?? p.default
        return (
          <label key={p.key} className="flex items-center gap-2 text-2xs">
            <span className="w-20 shrink-0 text-tv-text-muted">{p.label}</span>
            {p.type === 'number' && (
              <input
                type="number"
                value={Number(val)}
                min={p.min}
                max={p.max}
                step={p.step ?? 1}
                onChange={(e) => {
                  const n = Number(e.target.value)
                  if (!Number.isNaN(n)) onUpdate({ params: { [p.key]: n } })
                }}
                className="w-20 rounded border border-tv-border bg-tv-bg-secondary px-1.5 py-0.5 text-tv-text-primary focus:border-tv-blue focus:outline-none"
              />
            )}
            {p.type === 'boolean' && (
              <input
                type="checkbox"
                checked={Boolean(val)}
                onChange={(e) => onUpdate({ params: { [p.key]: e.target.checked } })}
                className="accent-tv-blue"
              />
            )}
          </label>
        )
      })}
      {config.lines.map((def) => {
        const color = ind.colors?.[def.key] ?? def.color
        return (
          <label key={def.key} className="flex items-center gap-2 text-2xs">
            <span className="w-20 shrink-0 text-tv-text-muted">{def.label}</span>
            <input
              type="color"
              value={color}
              onChange={(e) => onUpdate({ colors: { [def.key]: e.target.value } })}
              className="h-5 w-8 cursor-pointer rounded border border-tv-border bg-transparent p-0"
            />
            <span className="font-mono text-tv-text-muted">{color}</span>
          </label>
        )
      })}
    </div>
  )
}

// ── Active indicator row with drag-and-drop ───────────────────────────────────

function DraggableRow({
  index,
  onDragStart,
  onDragOver,
  onDrop,
  children,
}: {
  index: number
  onDragStart: (i: number) => void
  onDragOver: (e: React.DragEvent, i: number) => void
  onDrop: (i: number) => void
  children: React.ReactNode
}) {
  return (
    <div
      draggable
      onDragStart={() => onDragStart(index)}
      onDragOver={(e) => onDragOver(e, index)}
      onDrop={() => onDrop(index)}
      className="group flex items-center gap-1 rounded px-1 py-0.5 hover:bg-tv-bg-tertiary"
    >
      {children}
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export function IndicatorManager({
  builtinIndicators,
  customInstances,
  onAddBuiltin,
  onRemoveBuiltin,
  onUpdateBuiltin,
  onToggleBuiltin,
  onReorderBuiltin,
  onAttachCustom,
  onDetachCustom,
  onToggleCustom,
  onReorderCustom,
  onClose,
}: Props) {
  const { indicators: customDefinitions } = useCustomIndicatorStore()
  const { scripts: pineScripts } = usePineStore()
  const defMap = new Map(customDefinitions.map((d) => [d.id, d]))

  const [expandedInstanceId, setExpandedInstanceId] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState<'builtin' | 'custom'>('builtin')

  // Drag state
  const dragBuiltinRef = useRef<number | null>(null)
  const dragCustomRef = useRef<number | null>(null)

  // ── Drag handlers for built-in ─────────────────────────────────────────────
  function onBuiltinDragStart(i: number) {
    dragBuiltinRef.current = i
  }
  function onBuiltinDragOver(e: React.DragEvent, i: number) {
    e.preventDefault()
    if (dragBuiltinRef.current !== null && dragBuiltinRef.current !== i) {
      onReorderBuiltin(dragBuiltinRef.current, i)
      dragBuiltinRef.current = i
    }
  }
  function onBuiltinDrop(i: number) {
    if (dragBuiltinRef.current !== null && dragBuiltinRef.current !== i) {
      onReorderBuiltin(dragBuiltinRef.current, i)
    }
    dragBuiltinRef.current = null
  }

  // ── Drag handlers for custom ───────────────────────────────────────────────
  function onCustomDragStart(i: number) {
    dragCustomRef.current = i
  }
  function onCustomDragOver(e: React.DragEvent, i: number) {
    e.preventDefault()
    if (dragCustomRef.current !== null && dragCustomRef.current !== i) {
      onReorderCustom(dragCustomRef.current, i)
      dragCustomRef.current = i
    }
  }
  function onCustomDrop(i: number) {
    if (dragCustomRef.current !== null && dragCustomRef.current !== i) {
      onReorderCustom(dragCustomRef.current, i)
    }
    dragCustomRef.current = null
  }

  // ── Filtering for browse tab ───────────────────────────────────────────────
  const q = searchQuery.toLowerCase()
  const allBuiltin = getAllIndicators()
  const filteredBuiltin = q
    ? allBuiltin.filter(
        (e) =>
          e.config.name.toLowerCase().includes(q) || e.config.shortName.toLowerCase().includes(q)
      )
    : allBuiltin

  const filteredCustomDefs = q
    ? customDefinitions.filter((d) => d.name.toLowerCase().includes(q))
    : customDefinitions

  const totalActive = builtinIndicators.length + customInstances.length

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/50" onClick={onClose} aria-hidden="true" />

      {/* Panel */}
      <div className="fixed left-1/2 top-1/2 z-50 flex h-[90vh] max-h-[680px] w-[95vw] max-w-[860px] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-lg border border-tv-border bg-tv-bg-secondary shadow-2xl">
        {/* ── Left sidebar: active indicators ───────────────────────────────── */}
        <div className="flex w-64 shrink-0 flex-col border-r border-tv-border">
          <div className="border-b border-tv-border px-3 py-2.5">
            <p className="text-xs font-semibold text-tv-text-primary">
              On This Chart
              {totalActive > 0 && (
                <span className="ml-1.5 rounded bg-tv-blue/20 px-1.5 py-0.5 text-2xs font-semibold text-tv-blue">
                  {totalActive}
                </span>
              )}
            </p>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-2">
            {totalActive === 0 && (
              <p className="mt-6 text-center text-[10px] text-tv-text-muted">
                No indicators on this chart.
                <br />
                Browse the library →
              </p>
            )}

            {/* Built-in active */}
            {builtinIndicators.length > 0 && (
              <div className="mb-2">
                <p className="mb-1 px-1 text-[10px] font-medium uppercase tracking-wide text-tv-text-muted">
                  Built-in
                </p>
                {builtinIndicators.map((ind, i) => {
                  const entry = getIndicator(ind.id)
                  const firstLine = entry?.config.lines[0]
                  const dotColor = firstLine
                    ? (ind.colors?.[firstLine.key] ?? firstLine.color)
                    : '#888'
                  const label = entry
                    ? entry.config.params.length
                      ? `${entry.config.shortName} (${entry.config.params.map((p) => String(ind.params[p.key] ?? p.default)).join(', ')})`
                      : entry.config.shortName
                    : ind.id
                  const isExpanded = expandedInstanceId === ind.instanceId
                  const enabled = ind.enabled ?? true

                  return (
                    <div key={ind.instanceId}>
                      <DraggableRow
                        index={i}
                        onDragStart={onBuiltinDragStart}
                        onDragOver={onBuiltinDragOver}
                        onDrop={onBuiltinDrop}
                      >
                        {/* Drag handle */}
                        <GripVertical className="h-3 w-3 shrink-0 cursor-grab text-tv-text-muted opacity-0 group-hover:opacity-60" />
                        {/* Color dot */}
                        <span
                          className="h-2 w-2 shrink-0 rounded-full"
                          style={{ backgroundColor: enabled ? dotColor : '#555' }}
                        />
                        {/* Label */}
                        <span
                          className={cn(
                            'min-w-0 flex-1 truncate text-2xs',
                            enabled ? 'text-tv-text-primary' : 'text-tv-text-muted line-through'
                          )}
                        >
                          {label}
                          {entry && !entry.config.overlay && (
                            <span className="ml-1 text-tv-text-muted">(pane)</span>
                          )}
                        </span>
                        {/* Enable toggle */}
                        <button
                          type="button"
                          title={enabled ? 'Disable' : 'Enable'}
                          onClick={() => onToggleBuiltin(ind.instanceId)}
                          className={cn(
                            'flex h-4 w-4 shrink-0 items-center justify-center rounded text-[9px]',
                            enabled ? 'text-tv-green' : 'text-tv-text-muted'
                          )}
                        >
                          {enabled ? '●' : '○'}
                        </button>
                        {/* Settings */}
                        <button
                          type="button"
                          title="Settings"
                          onClick={() => setExpandedInstanceId(isExpanded ? null : ind.instanceId)}
                          className={cn(
                            'flex h-4 w-4 shrink-0 items-center justify-center rounded',
                            isExpanded
                              ? 'text-tv-blue'
                              : 'text-tv-text-muted hover:text-tv-text-primary'
                          )}
                        >
                          <Settings className="h-3 w-3" />
                        </button>
                        {/* Remove */}
                        <button
                          type="button"
                          title="Remove"
                          onClick={() => {
                            if (expandedInstanceId === ind.instanceId) setExpandedInstanceId(null)
                            onRemoveBuiltin(ind.instanceId)
                          }}
                          className="flex h-4 w-4 shrink-0 items-center justify-center rounded text-tv-text-muted hover:text-tv-red"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </DraggableRow>

                      {isExpanded && (
                        <div className="mb-2 ml-3 mt-1">
                          <BuiltinSettings
                            ind={ind}
                            onUpdate={(u) => onUpdateBuiltin(ind.instanceId, u)}
                          />
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}

            {/* Custom/Pine active */}
            {customInstances.length > 0 && (
              <div>
                <p className="mb-1 px-1 text-[10px] font-medium uppercase tracking-wide text-tv-text-muted">
                  Custom / Pine
                </p>
                {customInstances.map((inst, i) => {
                  const def = defMap.get(inst.definitionId)
                  const label = def?.name ?? `Unknown (${inst.definitionId.slice(0, 8)})`

                  return (
                    <DraggableRow
                      key={inst.instanceId}
                      index={i}
                      onDragStart={onCustomDragStart}
                      onDragOver={onCustomDragOver}
                      onDrop={onCustomDrop}
                    >
                      <GripVertical className="h-3 w-3 shrink-0 cursor-grab text-tv-text-muted opacity-0 group-hover:opacity-60" />
                      <span
                        className="h-2 w-2 shrink-0 rounded-full"
                        style={{ backgroundColor: inst.enabled ? (def?.color ?? '#888') : '#555' }}
                      />
                      <span
                        className={cn(
                          'min-w-0 flex-1 truncate text-2xs',
                          inst.enabled ? 'text-tv-text-primary' : 'text-tv-text-muted line-through'
                        )}
                      >
                        {label}
                      </span>
                      <button
                        type="button"
                        title={inst.enabled ? 'Disable' : 'Enable'}
                        onClick={() => onToggleCustom(inst.instanceId)}
                        className={cn(
                          'flex h-4 w-4 shrink-0 items-center justify-center rounded text-[9px]',
                          inst.enabled ? 'text-tv-green' : 'text-tv-text-muted'
                        )}
                      >
                        {inst.enabled ? '●' : '○'}
                      </button>
                      <button
                        type="button"
                        title="Remove"
                        onClick={() => onDetachCustom(inst.instanceId)}
                        className="flex h-4 w-4 shrink-0 items-center justify-center rounded text-tv-text-muted hover:text-tv-red"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </DraggableRow>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── Right pane: browse + add ───────────────────────────────────────── */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Header */}
          <div className="flex items-center gap-3 border-b border-tv-border px-4 py-2.5">
            <span className="text-sm font-semibold text-tv-text-primary">Indicators</span>
            <input
              type="text"
              placeholder="Search indicators…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 rounded border border-tv-border bg-tv-bg-primary px-2 py-1 text-xs text-tv-text-primary placeholder-tv-text-muted outline-none focus:border-tv-blue"
            />
            <button
              type="button"
              onClick={onClose}
              className="flex h-6 w-6 items-center justify-center rounded text-tv-text-muted hover:bg-tv-bg-tertiary hover:text-tv-text-primary"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-tv-border px-4">
            <button
              type="button"
              onClick={() => setActiveTab('builtin')}
              className={cn(
                'border-b-2 px-3 py-2 text-xs transition-colors',
                activeTab === 'builtin'
                  ? 'border-tv-blue text-tv-blue'
                  : 'border-transparent text-tv-text-muted hover:text-tv-text-primary'
              )}
            >
              Built-in ({allBuiltin.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('custom')}
              className={cn(
                'border-b-2 px-3 py-2 text-xs transition-colors',
                activeTab === 'custom'
                  ? 'border-tv-blue text-tv-blue'
                  : 'border-transparent text-tv-text-muted hover:text-tv-text-primary'
              )}
            >
              Custom JS ({customDefinitions.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('pine' as 'builtin' | 'custom')}
              className={cn(
                'border-b-2 px-3 py-2 text-xs transition-colors',
                activeTab === ('pine' as string)
                  ? 'border-tv-blue text-tv-blue'
                  : 'border-transparent text-tv-text-muted hover:text-tv-text-primary'
              )}
            >
              Pine ({pineScripts.length})
            </button>
          </div>

          {/* Browse content */}
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            {activeTab === 'builtin' && (
              <>
                {filteredBuiltin.length === 0 && (
                  <p className="text-center text-xs text-tv-text-muted">No indicators match.</p>
                )}
                {CATEGORY_ORDER.map((cat) => {
                  const items = filteredBuiltin.filter((e) => e.config.category === cat)
                  if (!items.length) return null
                  return (
                    <div key={cat} className="mb-5 last:mb-0">
                      <p className="mb-2 text-2xs font-medium uppercase tracking-wide text-tv-text-muted">
                        {CATEGORY_LABELS[cat]}
                      </p>
                      <div className="grid grid-cols-2 gap-1.5 xl:grid-cols-3">
                        {items.map(({ config }) => {
                          const instanceCount = builtinIndicators.filter(
                            (a) => a.id === config.id
                          ).length
                          return (
                            <button
                              key={config.id}
                              type="button"
                              onClick={() =>
                                onAddBuiltin({
                                  id: config.id,
                                  params: defaultParams(config),
                                  colors: {},
                                  enabled: true,
                                })
                              }
                              className="flex flex-col items-start rounded border border-tv-border bg-tv-bg-primary px-3 py-2 text-left transition-colors hover:border-tv-blue/40 hover:bg-tv-bg-tertiary"
                            >
                              <div className="flex w-full items-center justify-between">
                                <span className="text-xs font-semibold text-tv-text-primary">
                                  {config.shortName}
                                </span>
                                {instanceCount > 0 && (
                                  <span className="rounded bg-tv-blue/20 px-1 py-0.5 text-2xs font-semibold text-tv-blue">
                                    ×{instanceCount}
                                  </span>
                                )}
                              </div>
                              <span className="mt-0.5 text-2xs text-tv-text-muted">
                                {config.name}
                              </span>
                              {!config.overlay && (
                                <span className="mt-1 rounded bg-tv-bg-tertiary px-1 py-0.5 text-2xs text-tv-text-muted">
                                  Sub-pane
                                </span>
                              )}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </>
            )}

            {activeTab === ('pine' as string) && (
              <>
                {pineScripts.length === 0 && (
                  <div className="mt-8 text-center">
                    <p className="text-xs text-tv-text-muted">No Pine scripts yet.</p>
                    <p className="mt-1 text-2xs text-tv-text-muted">
                      Open the Pine editor to write or load an example.
                    </p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-1.5 xl:grid-cols-3">
                  {pineScripts.map((sc) => {
                    const attachedCount = customInstances.filter(
                      (i) => i.definitionId === sc.id
                    ).length
                    return (
                      <button
                        key={sc.id}
                        type="button"
                        onClick={() => onAttachCustom(sc.id)}
                        className="flex flex-col items-start rounded border border-tv-border bg-tv-bg-primary px-3 py-2 text-left transition-colors hover:border-tv-blue/40 hover:bg-tv-bg-tertiary"
                      >
                        <div className="flex w-full items-center justify-between">
                          <span className="text-xs font-semibold text-tv-text-primary">
                            {sc.title}
                          </span>
                          {attachedCount > 0 && (
                            <span className="rounded bg-tv-blue/20 px-1 py-0.5 text-2xs font-semibold text-tv-blue">
                              ×{attachedCount}
                            </span>
                          )}
                        </div>
                        {sc.description && (
                          <span className="mt-0.5 truncate text-2xs text-tv-text-muted">
                            {sc.description}
                          </span>
                        )}
                        <span className="mt-1 rounded bg-tv-bg-tertiary px-1 py-0.5 text-2xs text-tv-text-muted">
                          {sc.overlay ? 'Overlay' : 'Sub-pane'}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </>
            )}

            {activeTab === 'custom' && (
              <>
                {customDefinitions.length === 0 && (
                  <div className="mt-8 text-center">
                    <p className="text-xs text-tv-text-muted">No custom indicators yet.</p>
                    <p className="mt-1 text-2xs text-tv-text-muted">
                      Click <span className="font-mono text-tv-text-secondary">{'{ }'}</span> in the
                      toolbar to create one.
                    </p>
                  </div>
                )}
                {filteredCustomDefs.length === 0 && customDefinitions.length > 0 && (
                  <p className="text-center text-xs text-tv-text-muted">No indicators match.</p>
                )}
                <div className="grid grid-cols-2 gap-1.5 xl:grid-cols-3">
                  {filteredCustomDefs.map((def) => {
                    const attachedCount = customInstances.filter(
                      (i) => i.definitionId === def.id
                    ).length
                    return (
                      <button
                        key={def.id}
                        type="button"
                        onClick={() => onAttachCustom(def.id)}
                        className="flex flex-col items-start rounded border border-tv-border bg-tv-bg-primary px-3 py-2 text-left transition-colors hover:border-tv-blue/40 hover:bg-tv-bg-tertiary"
                      >
                        <div className="flex w-full items-center justify-between">
                          <div className="flex items-center gap-1.5">
                            <span
                              className="h-2 w-2 shrink-0 rounded-full"
                              style={{ backgroundColor: def.color }}
                            />
                            <span className="text-xs font-semibold text-tv-text-primary">
                              {def.name}
                            </span>
                          </div>
                          {attachedCount > 0 && (
                            <span className="rounded bg-tv-blue/20 px-1 py-0.5 text-2xs font-semibold text-tv-blue">
                              ×{attachedCount}
                            </span>
                          )}
                        </div>
                        {def.description && (
                          <span className="mt-0.5 truncate text-2xs text-tv-text-muted">
                            {def.description}
                          </span>
                        )}
                        <span className="mt-1 rounded bg-tv-bg-tertiary px-1 py-0.5 text-2xs text-tv-text-muted">
                          Overlay
                        </span>
                      </button>
                    )
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
